<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">{{$route.name}}</h1>
        <p class="text-sm text-slate-500">Administração e controle de processos licitatórios</p>
      </div>

      <!-- GRID DE LICITAÇÕES -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <!-- Toolbar do Grid -->
          <div class="bg-slate-700 px-4 py-4 flex items-center w-full">
              <div class="flex items-center gap-2 flex-none">
                <v-icon color="white" small>mdi-file-document-multiple</v-icon>
                <h2 class="text-white font-semibold uppercase tracking-wider" style="font-size: 1rem;">LICITAÇÕES</h2>
              </div>
              
              <div class="flex items-center gap-2 flex-1 justify-end">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button v-bind="attrs" v-on="on" @click="initialize" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                        <v-icon color="currentColor">mdi-history</v-icon>
                      </button>
                    </template>
                    <span>Atualizar</span>
                  </v-tooltip>

                  <div class="h-6 border-r border-slate-500 mx-2"></div>
                  
                  <div class="flex-none">
                    <button 
                      @click="$router.push('/biddings-manager/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                      style="width: auto !important; flex: none !important;"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-sm">NOVO</span>
                    </button>
                  </div>

                  <button @click="showSearch = !showSearch" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                     <v-icon color="currentColor">mdi-magnify</v-icon>
                  </button>
              </div>
          </div>

          <!-- Inline Search -->
           <v-expand-transition>
            <div v-if="showSearch" class="bg-slate-50 px-6 py-4 border-b border-slate-200 shadow-inner">
              <div class="flex items-center w-full px-3 py-2 bg-white border border-slate-300 rounded-lg shadow-sm focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all">
                  <v-icon color="#94a3b8" small class="mr-2">mdi-magnify</v-icon>
                  <input 
                    v-model="search" 
                    type="text" 
                    placeholder="Pesquisar por prefeitura, modalidade, tipo..." 
                    class="w-full text-sm text-slate-700 placeholder-slate-400 focus:outline-none bg-transparent border-none p-0"
                  >
              </div>
            </div>
           </v-expand-transition>

          <!-- Tabela -->
          <div class="overflow-x-auto">
             <div class="min-w-[1000px]">
               <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :loading="loader"
                hide-default-footer
                class="custom-table"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
              >
                <!-- ID -->
                <template v-slot:item.bidding_id="{ item }">
                  <span class="text-sm font-medium text-slate-400">#{{ item.bidding_id }}</span>
                </template>

                <!-- Prefeitura -->
                <template v-slot:item.prefecture_social_name="{ item }">
                    <span class="text-sm font-bold text-slate-700">{{ item.prefecture_social_name }}</span>
                </template>

                <!-- Modalidade -->
                <template v-slot:item.modality_desc="{ item }">
                    <span class="text-xs bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded border border-slate-200 uppercase">
                        {{ item.modality_desc }}
                    </span>
                </template>

                <!-- Tipo -->
                <template v-slot:item.type_desc="{ item }">
                   <span class="text-xs font-medium text-slate-600 uppercase">{{ item.type_desc }}</span>
                </template>

                <!-- Ações -->
                <template v-slot:item.action="{ item }">
                   <div class="flex justify-end gap-2">
                      <button 
                        @click="$router.push(`/biddings-manager/edit/${item.bidding_id}`)"
                        class="p-2 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-500 transition-colors"
                      >
                        <v-icon small color="currentColor">mdi-pencil</v-icon>
                      </button>
                      <button 
                        @click="deleteItem(item.bidding_id)"
                        class="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <v-icon small color="currentColor">mdi-delete</v-icon>
                      </button>
                   </div>
                </template>
               </v-data-table>

               <!-- Custom Pagination Footer -->
               <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium w-full">
                   <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                   
                   <div class="flex gap-1" v-if="pageCount > 1">
                       <button 
                         @click="page > 1 ? page-- : null" 
                         :disabled="page === 1"
                         class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         Anterior
                       </button>
                       
                       <button 
                         v-for="p in visiblePages" 
                         :key="p"
                         @click="page = p"
                         :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                         class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                       >
                         {{ p }}
                       </button>

                       <button 
                         @click="page < pageCount ? page++ : null" 
                         :disabled="page === pageCount"
                         class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         Próximo
                       </button>
                   </div>
               </div>
             </div>
          </div>
      </section>
    </div>

    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      {{snack.message}}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "CÓDIGO", value: "bidding_id", sortable: true, width: "100px", class: "text-uppercase caption font-weight-bold" },
        { text: "PREFEITURA", value: "prefecture_social_name", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "MODALIDADE", value: "modality_desc", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "TIPO", value: "type_desc", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "AÇÕES", align: "right", value: "action", sortable: false, class: "text-uppercase caption font-weight-bold" }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get("/biddings")
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/biddings/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
/* Utility Classes mimicking Tailwind */
.min-h-screen { min-height: 100vh; }
.p-4 { padding: 1rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
.mb-6 { margin-bottom: 1.5rem; }
.mb-8 { margin-bottom: 2rem; }
.gap-2 { gap: 0.5rem; }
.gap-4 { gap: 1rem; }
.w-full { width: 100%; }
.min-w-\[1000px\] { min-width: 1000px; }
.h-6 { height: 1.5rem; }

/* Flexbox */
.flex { display: flex; }
.flex-column { flex-direction: column; }
.items-center { align-items: center; }
.justify-between { justify-content: space-between; }
.justify-end { justify-content: flex-end; }

@media (min-width: 768px) {
  .md\:flex-row { flex-direction: row; }
}

/* Colors */
.text-slate-700 { color: #334155; }
.text-slate-500 { color: #64748b; }
.text-slate-400 { color: #94a3b8; }
.text-custom-gray { color: #9e9e9e !important; }
.text-white { color: #ffffff; }
.hover\:bg-slate-600:hover { background-color: #475569; }
.hover\:text-white:hover { color: #ffffff !important; }

.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-100 { background-color: #f1f5f9; }
.bg-slate-200 { background-color: #e2e8f0; }
.bg-slate-700 { background-color: #333333; }
.bg-white { background-color: #ffffff; }
.bg-orange-500 { background-color: #f97316; }
.hover\:bg-orange-600:hover { background-color: #ea580c; }

.border { border-style: solid; border-width: 1px; }
.border-slate-200 { border-color: #e2e8f0; }
.border-slate-300 { border-color: #cbd5e1; }
.border-slate-500 { border-color: #64748b; }
.border-b { border-bottom-width: 1px; }

/* Typography */
.font-bold { font-weight: 700; }
.font-semibold { font-weight: 600; }
.font-medium { font-weight: 500; }
.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.text-sm { font-size: 0.875rem; }
.text-xs { font-size: 0.75rem; }
.uppercase { text-transform: uppercase; }
.tracking-wider { letter-spacing: 0.05em; }

/* Components */
.rounded-xl { border-radius: 0.75rem; }
.rounded-lg { border-radius: 0.5rem; }
.rounded-md { border-radius: 0.375rem; }
.shadow-custom { box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); }
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
.overflow-hidden { overflow: hidden; }

/* Custom Overrides */
.custom-table ::v-deep th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.05em !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}

.custom-table ::v-deep td {
  font-size: 0.825rem !important;
  color: #334155 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  height: 60px !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}
</style>